import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const cardData = [
  { char: '!', icon: '◈', bg: 'from-rose-500 to-rose-700' },
  { char: '×', icon: '◉', bg: 'from-orange-500 to-orange-700' },
  { char: '!', icon: '◎', bg: 'from-amber-500 to-amber-700' },
];

const infoCards = [
  { icon: '⚠️', label: 'Status', value: 'Error' },
  { icon: '🔧', label: 'Action', value: 'Recover' },
  { icon: '🏠', label: 'Suggestion', value: 'Dashboard' },
];

/**
 * Fallback UI shown when an ErrorBoundary catches a render error.
 * Visual style aligned with NotFound page.
 */
export default function ErrorFallback({ error, showError = false }) {
  const navigate = useNavigate();
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      <main className="flex-1 flex flex-col items-center justify-center gap-10 px-6 py-16">
        <div className="flex gap-4 sm:gap-5">
          {cardData.map((card, i) => (
            <div
              key={i}
              style={{ transitionDelay: `${i * 80}ms` }}
              className={`
                relative w-36 h-28 sm:w-44 sm:h-32 rounded-2xl bg-gradient-to-br ${card.bg}
                overflow-hidden flex flex-col justify-end p-4 shadow-xl
                transition-all duration-500
                ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              `}
            >
              <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full bg-white/10" />
              <div className="absolute top-7 right-4 w-12 h-12 rounded-full bg-white/10" />
              <span className="relative z-10 text-white/90 text-base mb-1 leading-none">
                {card.icon}
              </span>
              <span className="relative z-10 text-white text-5xl sm:text-6xl font-extrabold leading-none tracking-tight">
                {card.char}
              </span>
            </div>
          ))}
        </div>

        <div
          className={`
            text-center max-w-md
            transition-all duration-700 delay-300
            ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}
          `}
        >
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800 mb-3 tracking-tight">
            Something Went Wrong
          </h1>
          <p className="text-slate-500 text-sm sm:text-base leading-relaxed">
            An unexpected error occurred while rendering this page.
            <br />
            You can reload and try again, or return to the dashboard.
          </p>
        </div>

        <div
          className={`
            flex flex-wrap justify-center gap-4
            transition-all duration-700 delay-500
            ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
          `}
        >
          {infoCards.map((info, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl shadow-md px-8 py-5 flex flex-col items-center gap-1 min-w-[130px]"
            >
              <span className="text-2xl">{info.icon}</span>
              <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest mt-1">
                {info.label}
              </span>
              <span className="text-xl font-bold text-slate-800">{info.value}</span>
            </div>
          ))}
        </div>

        {showError && error && (
          <details
            className={`
              max-w-lg w-full bg-white rounded-xl shadow-md px-4 py-3 text-left
              transition-all duration-700 delay-600
              ${animated ? 'opacity-100' : 'opacity-0'}
            `}
          >
            <summary className="text-sm font-semibold text-slate-600 cursor-pointer">
              Error details
            </summary>
            <pre className="mt-2 text-xs text-slate-500 overflow-auto whitespace-pre-wrap">
              {error.toString()}
            </pre>
          </details>
        )}

        <div
          className={`
            flex flex-wrap justify-center gap-4
            transition-all duration-700 delay-700
            ${animated ? 'opacity-100' : 'opacity-0'}
          `}
        >
          <button
            type="button"
            onClick={handleReload}
            className="
              bg-gradient-to-r from-blue-600 to-blue-800
              text-white px-8 py-3 rounded-xl text-sm font-semibold
              shadow-lg shadow-blue-200
              hover:shadow-blue-300 hover:-translate-y-0.5
              active:translate-y-0
              transition-all duration-150
            "
          >
            Reload Page
          </button>
          <button
            type="button"
            onClick={handleGoToDashboard}
            className="
              bg-white text-blue-600 border-2 border-blue-600
              px-8 py-3 rounded-xl text-sm font-semibold
              hover:bg-blue-50 hover:-translate-y-0.5
              active:translate-y-0
              transition-all duration-150
            "
          >
            Go to Dashboard
          </button>
        </div>
      </main>
    </div>
  );
}
